import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Users, AlertTriangle, CheckCircle, UserPlus } from 'lucide-react';

/**
 * Компонент показывает распределение нагрузки по менеджерам
 * Помогает руководителю видеть кто перегружен, а кто свободен
 */
const ProjectsByManager = ({ resourceData }) => {
  const navigate = useNavigate();
  if (!resourceData) {
    return (
      <Card data-testid="projects-by-manager-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" /> Нагрузка команды
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-gray-500 text-center py-4">Загрузка...</div>
        </CardContent>
      </Card>
    );
  }

  const {
    overloaded = 0,
    normal = 0,
    available = 0,
    managers = []
  } = resourceData;

  // Обработчик клика по категории - переход на проекты с фильтром
  const handleCategoryClick = (workloadStatus) => {
    // Находим ID менеджеров с данным статусом нагрузки
    const managerIds = managers
      .filter(m => m.workload_status === workloadStatus)
      .map(m => m.id);

    if (managerIds.length > 0) {
      // Переходим на страницу проектов с фильтром по менеджерам
      navigate(`/projects?responsible_ids=${managerIds.join(',')}`);
    }
  };

  // Получаем статус для отображения
  const getStatusBadge = (status, criticalCount) => {
    if (status === 'overloaded') {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
          Перегрузка
        </span>
      );
    }
    if (status === 'normal') {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
          Норма
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
        Свободен
      </span>
    );
  };

  return (
    <Card data-testid="projects-by-manager-card">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <Users className="w-5 h-5" /> Нагрузка команды
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Статистика по категориям нагрузки */}
          <div className="grid grid-cols-3 gap-2">
            <div
              className="p-2 bg-red-50 rounded-lg text-center cursor-pointer hover:bg-red-100 transition-colors"
              onClick={() => handleCategoryClick('overloaded')}
              title="Показать проекты перегруженных менеджеров"
            >
              <AlertTriangle className="w-5 h-5 mx-auto mb-1 text-red-600" />
              <div className="text-xl font-bold text-red-600">{overloaded}</div>
              <div className="text-xs text-gray-600">Перегрузка</div>
              <div className="text-xs text-gray-400">&gt;6 проектов</div>
            </div>

            <div
              className="p-2 bg-yellow-50 rounded-lg text-center cursor-pointer hover:bg-yellow-100 transition-colors"
              onClick={() => handleCategoryClick('normal')}
              title="Показать проекты менеджеров с нормальной нагрузкой"
            >
              <CheckCircle className="w-5 h-5 mx-auto mb-1 text-yellow-600" />
              <div className="text-xl font-bold text-yellow-600">{normal}</div>
              <div className="text-xs text-gray-600">Норма</div>
              <div className="text-xs text-gray-400">2-6 проектов</div>
            </div>

            <div
              className="p-2 bg-green-50 rounded-lg text-center cursor-pointer hover:bg-green-100 transition-colors"
              onClick={() => handleCategoryClick('available')}
              title="Показать проекты свободных менеджеров"
            >
              <UserPlus className="w-5 h-5 mx-auto mb-1 text-green-600" />
              <div className="text-xl font-bold text-green-600">{available}</div>
              <div className="text-xs text-gray-600">Свободны</div>
              <div className="text-xs text-gray-400">&lt;2 проектов</div>
            </div>
          </div>

          {/* Список менеджеров */}
          {managers.length > 0 && (
            <div className="pt-3 border-t">
              <p className="text-xs font-medium text-gray-500 mb-2">Топ по нагрузке:</p>
              <div className="space-y-1.5 max-h-40 overflow-y-auto">
                {managers.slice(0, 8).map((manager) => (
                  <div
                    key={manager.id}
                    className="flex items-center justify-between text-sm py-1 px-2 rounded hover:bg-gray-50"
                  >
                    <span className="truncate flex-1 mr-2" title={manager.name}>
                      {manager.name}
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500 text-xs whitespace-nowrap">
                        {manager.project_count} пр.
                        {manager.critical_count > 0 && (
                          <span className="text-red-500 ml-1">
                            ({manager.critical_count} крит.)
                          </span>
                        )}
                      </span>
                      {getStatusBadge(manager.workload_status, manager.critical_count)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default ProjectsByManager;
